import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import errorMonitoringService from '../../../services/errorMonitoringService';

const AlertManagementPanel = ({ errors, statistics }) => {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPattern, setSelectedPattern] = useState(null);

  useEffect(() => {
    loadPatterns();
  }, []);

  const loadPatterns = async () => {
    try {
      setLoading(true);
      const data = await errorMonitoringService?.getErrorPatterns();
      setPatterns(data || []);
    } catch (error) {
      console.error('Error loading patterns:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCriticalErrors = () => {
    return errors?.filter(e => e?.severity === 'critical' && e?.errorStatus !== 'resolved') || [];
  };

  const getUnresolvedErrors = () => {
    return errors?.filter(e => e?.errorStatus !== 'resolved') || [];
  };

  const getRecentErrors = () => {
    const last24Hours = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return errors?.filter(e => new Date(e?.occurredAt) > last24Hours) || [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const criticalErrors = getCriticalErrors();
  const unresolvedErrors = getUnresolvedErrors();
  const recentErrors = getRecentErrors();

  return (
    <div className="space-y-6">
      {/* Alert Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-red-100 rounded-lg">
              <Icon name="AlertTriangle" size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-900">{criticalErrors?.length}</p>
              <p className="text-xs text-red-700">Critical Alerts</p>
            </div>
          </div>
          <p className="text-xs text-red-600">Requires immediate attention</p>
        </div>

        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Icon name="AlertCircle" size={20} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-900">{unresolvedErrors?.length}</p>
              <p className="text-xs text-yellow-700">Unresolved Errors</p>
            </div>
          </div>
          <p className="text-xs text-yellow-600">Pending investigation</p>
        </div>

        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Icon name="Clock" size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-900">{recentErrors?.length}</p>
              <p className="text-xs text-blue-700">Last 24 Hours</p>
            </div>
          </div>
          <p className="text-xs text-blue-600">Recent activity</p>
        </div>
      </div>

      {/* Critical Errors List */}
      {criticalErrors?.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-neutral-900 mb-3 flex items-center gap-2">
            <Icon name="AlertTriangle" size={20} className="text-red-600" />
            Critical Errors Requiring Attention
          </h3>
          <div className="space-y-2">
            {criticalErrors?.slice(0, 5)?.map((error) => (
              <div
                key={error?.id}
                className="bg-white rounded-lg border border-red-200 p-4 hover:border-red-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded">
                        CRITICAL
                      </span>
                      {error?.errorCode && (
                        <span className="px-2 py-0.5 text-xs font-mono bg-neutral-100 text-neutral-700 rounded">
                          {error?.errorCode}
                        </span>
                      )}
                    </div>
                    <h4 className="text-sm font-semibold text-neutral-900 mb-1">
                      {error?.errorMessage}
                    </h4>
                    <div className="flex items-center gap-3 text-xs text-neutral-600">
                      <span className="flex items-center gap-1">
                        <Icon name="Package" size={12} />
                        {error?.affectedModule || 'Unknown'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Clock" size={12} />
                        {new Date(error?.occurredAt)?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <button className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition-colors">
                    Escalate
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Patterns */}
      <div>
        <h3 className="text-lg font-semibold text-neutral-900 mb-3 flex items-center gap-2">
          <Icon name="TrendingUp" size={20} className="text-blue-600" />
          Common Error Patterns
        </h3>
        <div className="space-y-3">
          {patterns?.slice(0, 5)?.map((pattern) => (
            <div
              key={pattern?.id}
              className="bg-white rounded-lg border border-neutral-200 p-4 hover:border-blue-300 transition-colors cursor-pointer"
              onClick={() => setSelectedPattern(selectedPattern?.id === pattern?.id ? null : pattern)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-sm font-semibold text-neutral-900">
                      {pattern?.patternName}
                    </h4>
                    {pattern?.autoRecoveryAvailable && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                        <Icon name="Zap" size={12} className="inline mr-1" />
                        Auto-Recovery
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <p className="text-neutral-600">Occurrences</p>
                      <p className="font-semibold text-neutral-900">{pattern?.occurrenceCount}</p>
                    </div>
                    <div>
                      <p className="text-neutral-600">Success Rate</p>
                      <p className="font-semibold text-green-600">{pattern?.resolutionSuccessRate?.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-neutral-600">Avg Resolution</p>
                      <p className="font-semibold text-blue-600">{pattern?.averageResolutionTimeMinutes} min</p>
                    </div>
                    <div>
                      <p className="text-neutral-600">Last Occurred</p>
                      <p className="font-semibold text-neutral-900">
                        {pattern?.lastOccurredAt ? new Date(pattern?.lastOccurredAt)?.toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                <Icon
                  name={selectedPattern?.id === pattern?.id ? 'ChevronUp' : 'ChevronDown'}
                  size={18}
                  className="text-neutral-400"
                />
              </div>

              {selectedPattern?.id === pattern?.id && (
                <div className="mt-4 pt-4 border-t border-neutral-200">
                  <h5 className="text-xs font-semibold text-neutral-700 mb-2">Common Resolution</h5>
                  <p className="text-xs text-neutral-600 mb-3">{pattern?.commonResolution}</p>
                  {pattern?.errorMessagePattern && (
                    <div>
                      <h5 className="text-xs font-semibold text-neutral-700 mb-1">Pattern Match</h5>
                      <code className="text-xs bg-neutral-100 px-2 py-1 rounded text-neutral-800">
                        {pattern?.errorMessagePattern}
                      </code>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Alert Configuration */}
      <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
        <h3 className="text-sm font-semibold text-neutral-900 mb-3 flex items-center gap-2">
          <Icon name="Settings" size={18} />
          Alert Configuration
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-900">Email Notifications</p>
              <p className="text-xs text-neutral-600">Send alerts via email for critical errors</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-900">SMS Alerts</p>
              <p className="text-xs text-neutral-600">Send SMS for critical system failures</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-900">Auto-Recovery</p>
              <p className="text-xs text-neutral-600">Attempt automated recovery for known issues</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertManagementPanel;
